import { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, GripVertical, Variable, Pencil, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { useFlowBuilderStore } from '@/stores/flow-builder-store';
import { toast } from 'sonner';
import type { CustomVariable, MMMCard } from '@/types/field-mapping-flow';

// ── Dependency Checker ──

function checkVariableUsage(
  varId: string,
  fieldMappings: Record<string, unknown>,
  branchId: string
): string[] {
  const usages: string[] = [];
  const searchStr = `$.variables.${varId}`;

  const mapping = (fieldMappings as Record<string, { fields?: Record<string, MMMCard>; variables?: Record<string, CustomVariable> }>)[branchId];
  if (!mapping) return usages;

  // Check all MMM cards
  if (mapping.fields) {
    Object.values(mapping.fields).forEach((card) => {
      if (typeof card !== 'object' || card === null || !('cardId' in card)) return;
      const mmmCard = card as MMMCard;
      const cardLabel = mmmCard.displayLabel || mmmCard.saasFieldModuleName || 'Card';

      if (mmmCard.fields) {
        Object.entries(mmmCard.fields).forEach(([fieldName, fieldValue]) => {
          if (typeof fieldValue === 'string' && fieldValue.includes(searchStr)) {
            usages.push(`${cardLabel} (Create) → ${fieldName}`);
          }
        });
      }

      if (mmmCard.fieldsForExisting?.fields) {
        Object.entries(mmmCard.fieldsForExisting.fields).forEach(([fieldName, fieldValue]) => {
          if (typeof fieldValue === 'string' && fieldValue.includes(searchStr)) {
            usages.push(`${cardLabel} (Update) → ${fieldName}`);
          }
        });
      }

      if (mmmCard.fields_formats && JSON.stringify(mmmCard.fields_formats).includes(searchStr)) {
        usages.push(`${cardLabel} → Field Formats`);
      }
      if (mmmCard.fieldsForExisting?.fields_formats && JSON.stringify(mmmCard.fieldsForExisting.fields_formats).includes(searchStr)) {
        usages.push(`${cardLabel} (Update) → Field Formats`);
      }
    });
  }

  // Check other variables
  if (mapping.variables) {
    Object.values(mapping.variables).forEach((otherVar) => {
      if (otherVar.id === varId) return;
      if (otherVar.value && otherVar.value.includes(searchStr)) {
        usages.push(`Variable "${otherVar.name}" → Value`);
      }
      if (otherVar.fields_formats && JSON.stringify(otherVar.fields_formats).includes(searchStr)) {
        usages.push(`Variable "${otherVar.name}" → Formats`);
      }
    });
  }

  return usages;
}

// ── Main Panel ──

export function VariablesPanel() {
  const selectedBranchId = useFlowBuilderStore((s) => s.selectedBranchId);
  const fieldMappings = useFlowBuilderStore((s) => s.fieldMappings);
  const removeVariable = useFlowBuilderStore((s) => s.removeVariable);
  const saveBranch = useFlowBuilderStore((s) => s.saveBranch);
  const selectBranch = useFlowBuilderStore((s) => s.selectBranch);
  const openVariableForm = useFlowBuilderStore((s) => s.openVariableForm);

  // Auto-select the default branch if no branch is selected
  useEffect(() => {
    if (selectedBranchId) return;
    const entries = Object.entries(fieldMappings);
    if (entries.length === 0) return;
    const defaultEntry = entries.find(([, m]) => m.filterValue === '---default');
    if (defaultEntry) {
      selectBranch(defaultEntry[0]);
    } else {
      selectBranch(entries[0][0]);
    }
  }, [selectedBranchId, fieldMappings, selectBranch]);

  const currentMapping = selectedBranchId ? fieldMappings[selectedBranchId] : null;
  const variables = currentMapping?.variables || {};
  const variablesList = useMemo(
    () => Object.values(variables).sort((a, b) => (a.orderOf ?? 0) - (b.orderOf ?? 0)),
    [variables]
  );

  const handleDragStart = (e: React.DragEvent, varItem: CustomVariable) => {
    const fieldReference = `\${{{$.variables.${varItem.id}}}}`;
    e.dataTransfer.setData('text/plain', fieldReference);
    e.dataTransfer.setData('application/x-field-path', `$.variables.${varItem.id}`);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleOpenAdd = () => {
    openVariableForm(null, true);
  };

  const handleOpenEdit = (varItem: CustomVariable) => {
    openVariableForm(varItem.id, false);
  };

  const handleDelete = (e: React.MouseEvent, varId: string) => {
    e.stopPropagation();
    if (!selectedBranchId) return;

    const usages = checkVariableUsage(varId, fieldMappings, selectedBranchId);
    if (usages.length > 0) {
      toast.error(
        `Cannot delete: variable is used in ${usages.length} place${usages.length > 1 ? 's' : ''}`,
        {
          description: usages.slice(0, 3).join(', ') + (usages.length > 3 ? `, +${usages.length - 3} more` : ''),
          duration: 5000,
        }
      );
      return;
    }

    removeVariable(selectedBranchId, varId);
    // Auto-save to Firestore after deletion (matching original app behavior)
    saveBranch(selectedBranchId);
  };

  const branchOptions = useMemo(() => {
    return Object.entries(fieldMappings).map(([id, m]) => ({
      value: id,
      label: m.filterValueLabel || m.name || id,
    }));
  }, [fieldMappings]);

  const hasFormationSteps = (varItem: CustomVariable) => {
    return varItem.fields_formats && Object.keys(varItem.fields_formats).length > 0;
  };

  return (
    <div className="flex h-full flex-col">
      {/* Branch selector */}
      {branchOptions.length > 0 && (
        <div className="px-3 pt-2 pb-1">
          <Select
            value={selectedBranchId || ''}
            onValueChange={(v) => selectBranch(v || null)}
            options={branchOptions}
            placeholder="Select branch..."
            className="w-full"
            triggerClassName="h-7 px-2.5 text-[11px] rounded-md"
          />
        </div>
      )}

      {/* Variables list */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 pt-1">
        {/* No branch selected */}
        {!selectedBranchId && (
          <div className="py-6 text-center">
            <Variable className="mx-auto mb-2 h-6 w-6 text-muted-foreground/40" />
            <p className="text-xs text-muted-foreground">
              Select a branch to manage variables
            </p>
          </div>
        )}

        {/* Empty state */}
        {selectedBranchId && variablesList.length === 0 && (
          <div className="py-6 text-center">
            <Variable className="mx-auto mb-2 h-6 w-6 text-muted-foreground/40" />
            <p className="text-xs text-muted-foreground">No custom variables</p>
            <p className="mt-1 text-[10px] text-muted-foreground/70 leading-relaxed">
              Variables let you store computed values and reuse them across field mappings.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 gap-1 text-[10px] h-7"
              onClick={handleOpenAdd}
            >
              <Plus className="h-3 w-3" />
              Add Variable
            </Button>
          </div>
        )}

        {/* Variable items */}
        {selectedBranchId && variablesList.length > 0 && (
          <div className="space-y-1.5">
            {variablesList.map((varItem) => (
              <div
                key={varItem.id}
                draggable
                onDragStart={(e) => handleDragStart(e, varItem)}
                onClick={() => handleOpenEdit(varItem)}
                className="group flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-2 transition-colors hover:bg-muted/50 active:cursor-grabbing"
              >
                <GripVertical className="h-3 w-3 shrink-0 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors cursor-grab" />
                <Variable className="h-3.5 w-3.5 shrink-0 text-purple-500" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[11px] font-medium text-foreground leading-tight">
                    {varItem.name}
                  </p>
                  {varItem.value && (
                    <p className="truncate text-[10px] text-muted-foreground/60 font-mono leading-tight mt-0.5">
                      {varItem.value.length > 40 ? varItem.value.substring(0, 40) + '...' : varItem.value}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                  {hasFormationSteps(varItem) && (
                    <div className="rounded p-1 text-purple-400" title="Has formation steps">
                      <Wand2 className="h-2.5 w-2.5" />
                    </div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenEdit(varItem);
                    }}
                    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    title="Edit"
                  >
                    <Pencil className="h-2.5 w-2.5" />
                  </button>
                  <button
                    onClick={(e) => handleDelete(e, varItem.id)}
                    className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-2.5 w-2.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add button footer */}
      {selectedBranchId && variablesList.length > 0 && (
        <div className="border-t border-border px-3 py-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-1.5 text-[11px] text-muted-foreground"
            onClick={handleOpenAdd}
          >
            <Plus className="h-3 w-3" />
            Add Variable
          </Button>
        </div>
      )}
    </div>
  );
}
